/* eslint-disable radix */
import React, { useCallback, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { v4 as uuid } from 'uuid';
import { Alert, Button, Empty, LayoutSidebar, SectionHeader } from 'components';
import { EmployeeLaborJobErrorType, IEmployeeLaborJobError, IEmployeeLaborJobState } from 'screens/MakePayment/Calculator/types';
import { selectSiteSettings } from 'store/Site/selectors';
import { selectCalculatorChangeLog, selectCalculatorPaymentSet, selectCalculatorPOSResults, setPOSResults } from 'store/TipCalculator';
import { ChangeLogAction, ICalculatorRules, IEmployerSite, IPOSData, IPOSProvider, ITipCalculatorChangeLogEntry, TicketOverrideType } from 'types';
import { sortAlphaNumerically } from 'utils';
import { Duration } from 'utils/duration';
import { buildLaborChangeLogEntries, getSharedPOSJobTitles, lastRemainingPayeeInSharePolicy } from 'utils/tipCalculator';
import { MPContent } from '../../components';
import EmployeeListItem from '../components/LaborData/EmployeeListItem/EmployeeListItem';
import Summary, { IDaySegmentSummary } from '../components/LaborData/Summary/Summary';
import { CalculateProcessingModal } from '../components/modals/CalculateProcessingModal';

interface ILaborDataProps {
  currentCalculatorRules: ICalculatorRules;
  currentSite: IEmployerSite;
  onBack: () => void;
  onExpand: () => void;
  pageHeaderContent: React.ReactNode;
  sidebarOpen?: boolean;
}

interface ILaborDataPayee {
  id: string;
  name: string;
  labor: IEmployeeLaborJobState[];
  provider: IPOSProvider;
  isSharePOSRemovable: boolean;
  hasSplitChecks: boolean;
  dataIndex: number;
  autoClockOut: boolean;
}

const getTotalSegmentMinutes = (start: string, end: string) => {
  try {
    const segmentDurationMinutes = Duration.convertDurationStringToMins(end) - Duration.convertDurationStringToMins(start);
    if (segmentDurationMinutes < 0) {
      const hours = Math.abs(segmentDurationMinutes) / 60;
      return Math.round((24 - hours) * 60);
    }

    return segmentDurationMinutes;
  } catch {
    return -1;
  }
};

const LaborData = ({ currentCalculatorRules, currentSite, onBack, onExpand, pageHeaderContent, sidebarOpen }: ILaborDataProps) => {
  const dispatch = useDispatch();

  const { timezone: currentSiteTimezone, holdback: currentSiteHoldbacks, tipCalculatorPercentFee: currentSiteTipCalcPercentFee } = useSelector(selectSiteSettings()) || {};
  const paymentSet = useSelector(selectCalculatorPaymentSet());
  const paymentSetSegments = paymentSet?.segments;
  const paymentDate = paymentSet?.date;

  /**
   * this clone is used throughout the component to store any
   * changes made and will be compared to the original to determine
   * if the user has made any changes
   */
  const posResults = useSelector(selectCalculatorPOSResults());
  const [posDataClone, setPOSDataClone] = useState(structuredClone(posResults));
  const dirty = useMemo(() => !isEqual(posResults, posDataClone), [posResults, posDataClone]);

  // Change log entries
  const changeLogEntries = useSelector(selectCalculatorChangeLog());
  const [currentChangeLogEntries, setCurrentChangeLogEntries] = useState<ITipCalculatorChangeLogEntry[]>(changeLogEntries);

  const isSharedPOS = (data: IPOSData): boolean => {
    const sharedPOSJobTitles = getSharedPOSJobTitles(currentCalculatorRules);
    return !data.employee && data.provider.schedules.length > 0 && sharedPOSJobTitles.includes(data.provider.schedules[0].jobTitle.toLowerCase());
  };

  const mappedSummaryData = useMemo((): IDaySegmentSummary[] => {
    const segmentTotals: IDaySegmentSummary[] = [];

    // initialize each segment to 0
    paymentSetSegments.forEach((segment) => {
      segmentTotals.push({ totalJobMinutes: 0, segment });
    });

    // calculate total minutes for each segment
    posDataClone.data.forEach((data) => {
      if (data.segments && !isSharedPOS(data)) {
        data.segments.forEach((seg) => {
          const match = segmentTotals.find((item) => item.segment.id === seg.id);
          const segmentJobTitle = seg.job.title.toLowerCase().trim();
          if (match && seg.totals.totalMinWorked > 0 && currentCalculatorRules.rules.some((rule) => rule.contributor && rule.contributor.name.toLowerCase().trim() === segmentJobTitle)) {
            match.totalJobMinutes += seg.totals.totalMinWorked;
          }
        });
      }
    });

    return segmentTotals;
  }, [posDataClone, paymentSetSegments]);

  const totalTippedMinutes = useMemo(() => mappedSummaryData.reduce((acc, summary) => acc + summary.totalJobMinutes, 0), [mappedSummaryData]);
  const hasActiveErrors = useMemo(() => posDataClone.data.some((data) => data.totals.totalMinWorked < 0 || data.segments.some((segment) => segment.totals.totalMinWorked < 0)), [posDataClone]);

  const validateJobData = (jobData: IEmployeeLaborJobState[]) =>
    jobData.map((job) => {
      // persist existing duration formatting error
      if (job.duration < 0 && job.error?.field === 'duration') {
        return job;
      }

      // validate that the duration of a job is not longer than the selected day-segment's duration
      if (job.duration && job.daySegment) {
        const maxDurationMinutes = getTotalSegmentMinutes(job.daySegment.start, job.daySegment.end);

        if (maxDurationMinutes < 0) {
          return {
            ...job,
            error: {
              type: EmployeeLaborJobErrorType.InvalidData,
              message: 'Invalid duration format. Expected hh:mm.',
              field: 'duration',
            } as IEmployeeLaborJobError,
          };
        }

        if (job.duration > maxDurationMinutes) {
          return {
            ...job,
            error: {
              type: EmployeeLaborJobErrorType.InvalidData,
              message: 'Duration can not be longer than the selected day segment.',
              field: 'duration',
            } as IEmployeeLaborJobError,
          };
        }
      }

      // validate that the total duration for an employee does not exceed 24 hours
      const totalDuration = jobData.reduce((acc, data) => acc + data.duration, 0);
      if (totalDuration > Duration.MAX_DAILY_MINUTES) {
        return {
          ...job,
          error: {
            type: EmployeeLaborJobErrorType.InvalidData,
            message: 'Total duration for an employee can not be over 24 hours.',
            field: 'duration',
          } as IEmployeeLaborJobError,
        };
      }

      return { ...job, error: undefined };
    });

  const payeesList = useMemo(() => {
    // construct the payees
    const payees: ILaborDataPayee[] = [];

    posDataClone.data.forEach((data, idx) => {
      const laborData: IEmployeeLaborJobState[] = [];

      // do not include shared point of sales employees in the list of payees on the labor page
      if (isSharedPOS(data)) return;

      if (paymentSetSegments.length && data.segments.length > 0) {
        // build labor data from segments
        data.segments.forEach((seg) => {
          const segment = paymentSetSegments?.find((s) => s.id === seg.id);
          const segmentJobTitle = seg.job.title.toLowerCase().trim();
          const ruleExists = currentCalculatorRules.rules.some((rule) => rule.contributor && rule.contributor.name.toLowerCase().trim() === segmentJobTitle);

          if (segment && ruleExists) {
            laborData.push({
              daySegment: segment,
              jobTitle: seg.job,
              duration: seg.totals.totalMinWorked,
              total: seg.totals.tip.net,
              error: undefined,
              id: uuid(),
            });
          }
        });
      }

      const autoClockOut = data.provider?.schedules.some((item) => item.clockedOut === false);

      // Check if the employee has any split checks they are the owner of or receiving from.
      const hasSplitChecks =
        data.segments.some((segment) => segment.tickets.some((ticket) => ticket.override?.type === TicketOverrideType.SPLIT)) ||
        posDataClone.data.some((item) =>
          item.segments.some((segment) =>
            segment.tickets.some((ticket) => ticket.override?.type === TicketOverrideType.SPLIT && ticket.override?.shares.some((share) => share.participant.id === data.employee?.id || share.participant.id === data.provider.employeeId))
          )
        );

      const payee = {
        id: data.employee?.id || data.provider.employeeId,
        name: data.employee?.name || data.provider.name,
        labor: laborData,
        provider: data.provider,
        isSharePOSRemovable: !lastRemainingPayeeInSharePolicy(data.employee?.id || data.provider.employeeId, currentCalculatorRules, posDataClone, paymentSetSegments),
        hasSplitChecks,
        dataIndex: idx,
        autoClockOut,
      };

      if (payee.labor.length) payees.push(payee);
    });

    // sort by employee first name or provider first name
    return payees.sort(sortAlphaNumerically('name'));
  }, [posDataClone, currentCalculatorRules, paymentSetSegments, paymentDate]);

  const handleRemoveEmployee = (payee: ILaborDataPayee) => {
    setCurrentChangeLogEntries(buildLaborChangeLogEntries(ChangeLogAction.REMOVED, currentChangeLogEntries, { id: payee.id, name: payee.name }));
    setPOSDataClone({ ...posDataClone, data: posDataClone.data.filter((_, i) => i !== payee.dataIndex) });
  };

  const handleJobChange = (payee: ILaborDataPayee, jobData: IEmployeeLaborJobState[], deletedJob?: IEmployeeLaborJobState) => {
    // update duration
    jobData.forEach((data) => {
      const duration = data.error ? -1 : data.duration;
      const segment = posDataClone.data[payee.dataIndex].segments.find((s) => s.id === data.daySegment.id && s.job.title.toLowerCase() === data.jobTitle.title.toLowerCase());

      if (segment && segment.totals.totalMinWorked !== duration) {
        setCurrentChangeLogEntries(
          buildLaborChangeLogEntries(ChangeLogAction.SEGMENT_DURATION_EDITED, currentChangeLogEntries, {
            id: payee.id,
            name: payee.name,
            segmentName: segment.name,
            jobTitle: data.jobTitle.title.toLowerCase(),
            from: segment.totals.totalMinWorked,
            to: duration,
          })
        );

        // Update the segment duration
        segment.totals.totalMinWorked = duration;
      }
    });

    // Remove deleted job
    if (deletedJob) {
      const segmentIndex = posDataClone.data[payee.dataIndex].segments.findIndex((s) => s.id === deletedJob.daySegment.id && s.job.title.toLowerCase() === deletedJob.jobTitle.title.toLowerCase());
      if (segmentIndex >= 0) {
        setCurrentChangeLogEntries(
          buildLaborChangeLogEntries(ChangeLogAction.SEGMENT_REMOVED, currentChangeLogEntries, { id: payee.id, name: payee.name, segmentName: deletedJob.daySegment.name, jobTitle: deletedJob.jobTitle.title.toLowerCase() })
        );
        posDataClone.data[payee.dataIndex].segments.splice(segmentIndex, 1);
      }
    }

    setPOSDataClone({ ...posDataClone });
  };

  const [showIntegrationDataProcessingModal, hideIntegrationDataProcessingModal] = useModal(
    () => (
      <CalculateProcessingModal
        tipCalcRules={currentCalculatorRules}
        onClose={hideIntegrationDataProcessingModal}
        businessDate={paymentDate}
        siteId={currentSite.id}
        siteHoldbackSettings={currentSiteHoldbacks}
        segments={paymentSetSegments}
        posDataResults={posDataClone}
        creditCardPercentFee={currentSiteTipCalcPercentFee}
        changeLogEntries={currentChangeLogEntries}
        onSuccess={() => dispatch(setPOSResults(posDataClone))}
      />
    ),
    [currentCalculatorRules, paymentDate, currentSite, currentSiteHoldbacks, paymentSet, posDataClone]
  );

  const [showAbandonChangesModal, hideAbandonChangesModal] = useModal(
    () => (
      <Alert
        data-cy="abandon-changes-alert"
        title="Abandon Changes?"
        message="You have unsaved changes that haven't been applied to your calculations. Are you sure you want to discard edits?"
        onLeftAction={hideAbandonChangesModal}
        onRightAction={onBack}
        leftActionLabel="Cancel"
        rightActionLabel="Abandon Changes"
        rightActionAppearance="destructive"
        isActive
      />
    ),
    [onBack]
  );

  const primarySummaryAction = useCallback(
    (device: 'mobile' | 'desktop') => (
      <Button
        data-cy="labor-data-next-button"
        label="Recalculate"
        type="button"
        appearance="primary"
        disabled={hasActiveErrors || !posDataClone.data.length || !dirty || totalTippedMinutes <= 0}
        size={device === 'mobile' ? 'small' : 'large'}
        onClick={() => showIntegrationDataProcessingModal()}
      />
    ),
    [hasActiveErrors, posDataClone, dirty]
  );

  const secondarySummaryAction = useCallback(
    (device: 'mobile' | 'desktop') => <Button data-cy="labor-data-back-button" label="Back" type="button" appearance="white" size={device === 'mobile' ? 'small' : 'large'} onClick={dirty ? showAbandonChangesModal : onBack} />,
    [onBack, dirty]
  );

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}

          <MPContent data-cy="labor-data-list">
            <SectionHeader data-cy="labor-data-header" title={`Labor Data (${payeesList.length})`} />
            {!payeesList.length ? (
              <Empty container containerHeight={250} icon="People" message="No Payees" />
            ) : (
              payeesList.map((payee) => (
                <EmployeeListItem
                  key={`payee-${payee.id}-${payee.provider.employeeId}`}
                  laborData={payee.labor.length ? payee.labor : undefined}
                  timecardData={payee.provider}
                  paymentDate={paymentDate}
                  timezone={currentSiteTimezone}
                  userId={payee.provider?.employeeId || payee.id}
                  username={payee.name}
                  onRemoveEmployee={() => handleRemoveEmployee(payee)}
                  removeDisabled={payeesList.length === 1}
                  onJobChange={(jobData: IEmployeeLaborJobState[], deletedJob?: IEmployeeLaborJobState) => handleJobChange(payee, jobData, deletedJob)}
                  validate={validateJobData}
                  isShareRemovable={payee.isSharePOSRemovable}
                  autoClockOut={payee.autoClockOut}
                  hasSplitChecks={payee.hasSplitChecks}
                />
              ))
            )}
          </MPContent>
        </>
      }
      sidebar={<Summary date={paymentDate} onExpand={onExpand} primaryActionBtn={primarySummaryAction} secondaryActionBtn={secondarySummaryAction} segments={mappedSummaryData} sidebarOpen={sidebarOpen} totalMinutes={totalTippedMinutes} />}
      sidebarOpen={sidebarOpen}
    />
  );
};

export default LaborData;
